import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { addTruck, subscribeTrucks, updateTruck, removeTruck, upsertTruckByVehicleNumber } from '../../../data/trucksData';
import { supabase } from '../../../lib/supabase';

// ─── CSV helpers ─────────────────────────────────────────────────────────────
const VEHICLE_CSV_HEADERS = ['VehicleNumber', 'Capacity_MT', 'CurrentLocation', 'Rate_per_Ton', 'Status', 'Verified', 'Remark'];

const VEHICLE_SAMPLE_ROWS = [
  ['MH12AB1234', '25', '"Pune, Maharashtra"', '1200', 'Available', 'Yes', ''],
  ['GJ05CD5678', '30', '"Surat, Gujarat"', '1350', 'Busy', 'No', 'update'],
  ['TN09EF9012', '20', '"Chennai, Tamil Nadu"', '1150', 'Available', 'Yes', 'update'],
];

function downloadSampleCSV() {
  const rows = [VEHICLE_CSV_HEADERS, ...VEHICLE_SAMPLE_ROWS];
  const csv = rows?.map(r => r?.join(','))?.join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'vehicle_management_sample.csv';
  a?.click();
  URL.revokeObjectURL(url);
}

function downloadCurrentDataCSV(trucks) {
  const headers = VEHICLE_CSV_HEADERS;
  const dataRows = trucks?.map(t => [
    t?.vehicleNumber || '',
    t?.capacityMT || '',
    t?.currentLocation || '',
    t?.ratePerTon || '',
    t?.status || '',
    t?.verified ? 'Yes' : 'No',
    '',
  ]?.map(v => `"${String(v)?.replace(/"/g, '""')}"`)?.join(','));
  const csv = [headers?.join(','), ...dataRows]?.join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const date = new Date()?.toISOString()?.split('T')?.[0];
  a.download = `vehicles_current_data_${date}.csv`;
  a?.click();
  URL.revokeObjectURL(url);
}

function parseCSV(text) {
  const lines = text?.trim()?.split('\n')?.filter(l => l?.trim());
  if (lines?.length < 2) return [];
  const headers = parseCSVLine(lines?.[0]);
  return lines?.slice(1)?.map(line => {
    const vals = parseCSVLine(line);
    const obj = {};
    headers?.forEach((h, i) => { obj[h] = vals?.[i] ?? ''; });
    return obj;
  });
}

function parseCSVLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line?.length; i++) {
    const ch = line?.[i];
    if (ch === '"') {
      if (inQuotes && line?.[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      result?.push(current?.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  result?.push(current?.trim());
  return result;
}

function downloadResultsCSV(rows, resultRows) {
  const headers = [...VEHICLE_CSV_HEADERS, 'Update_Status', 'Message'];
  const dataRows = rows?.map((row, i) => {
    const r = resultRows?.[i] || {};
    return [
      row?.VehicleNumber || '',
      row?.Capacity_MT || '',
      row?.CurrentLocation || '',
      row?.Rate_per_Ton || '',
      row?.Status || '',
      row?.Verified || '',
      row?.Remark || '',
      r?.status || '',
      r?.message || '',
    ]?.map(v => `"${String(v)?.replace(/"/g, '""')}"`)?.join(',');
  });
  const csv = [headers?.join(','), ...dataRows]?.join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const date = new Date()?.toISOString()?.split('T')?.[0];
  a.download = `upload_results_${date}.csv`;
  a?.click();
  URL.revokeObjectURL(url);
}

// ─── CSV Upload Modal ─────────────────────────────────────────────────────────
const VehicleCSVUploadModal = ({ onClose, onUpload }) => {
  const fileRef = useRef(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleFile = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (!file?.name?.endsWith('.csv')) { setError('Please upload a .csv file.'); return; }
    setError('');
    setLoading(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const rows = parseCSV(ev?.target?.result);
        const processed = await onUpload(rows);
        setResult(processed);
        // Auto-download results CSV
        downloadResultsCSV(rows, processed?.resultRows);
      } catch (err) {
        console.error('CSV upload error:', err);
        setError('Failed to parse or upload CSV. Please check the format.');
      }
      setLoading(false);
    };
    reader?.readAsText(file);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
      <div className="rounded-2xl shadow-xl w-full max-w-md" style={{ backgroundColor: '#fdf6ec', border: '1px solid #e5d9cc' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: '#e5d9cc', backgroundColor: '#f8f1e9', borderRadius: '1rem 1rem 0 0' }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: '#fef3c7' }}>
              <Icon name="Upload" size={18} color="#d97706" />
            </div>
            <h2 className="text-base font-bold" style={{ color: '#1f2937' }}>Bulk Upload — Vehicle Management</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-amber-100">
            <Icon name="X" size={18} color="#6b7280" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          <div className="rounded-lg p-3 text-xs" style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}>
            <p className="font-semibold mb-1" style={{ color: '#92400e' }}>Required CSV Columns (7):</p>
            <p style={{ color: '#78350f' }}>VehicleNumber, Capacity_MT, CurrentLocation, Rate_per_Ton, Status (Available/Busy), Verified (Yes/No), Remark</p>
            <p className="mt-1.5 font-medium" style={{ color: '#92400e' }}>💡 Remark = "update" → updates existing record. Blank = skip.</p>
            <p className="mt-1 font-medium" style={{ color: '#92400e' }}>⚠️ If CurrentLocation has a comma (e.g. Pune, Maharashtra), wrap it in quotes in your CSV.</p>
          </div>

          <button onClick={downloadSampleCSV} className="flex items-center gap-2 text-sm font-semibold underline" style={{ color: '#d97706' }}>
            <Icon name="Download" size={15} />
            Download Sample CSV
          </button>

          {!result && (
            <div
              onClick={() => fileRef?.current?.click()}
              className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed cursor-pointer py-8 transition-colors hover:bg-amber-50"
              style={{ borderColor: '#f59e0b' }}
            >
              <Icon name="FileText" size={32} color="#f59e0b" />
              <p className="text-sm font-semibold" style={{ color: '#374151' }}>Click to select CSV file</p>
              <p className="text-xs" style={{ color: '#9ca3af' }}>.csv files only</p>
              <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFile} />
            </div>
          )}

          {loading && <p className="text-sm text-center" style={{ color: '#6b7280' }}>Processing…</p>}
          {error && <p className="text-sm font-medium" style={{ color: '#dc2626' }}>{error}</p>}

          {result && (
            <div className="rounded-xl p-4 space-y-2" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <div className="flex items-center gap-2 mb-1">
                <Icon name="CheckCircle" size={18} color="#16a34a" />
                <p className="font-bold text-sm" style={{ color: '#15803d' }}>Upload Complete</p>
              </div>
              <p className="text-base font-semibold" style={{ color: '#166534' }}>
                {result?.updated} row{result?.updated !== 1 ? 's' : ''} updated successfully, {result?.skipped} row{result?.skipped !== 1 ? 's' : ''} skipped
              </p>
              {result?.invalid > 0 && <p className="text-sm" style={{ color: '#b45309' }}>⚠️ Invalid: <strong>{result?.invalid}</strong> row{result?.invalid !== 1 ? 's' : ''} (missing required fields)</p>}
              <div className="flex items-center gap-1.5 mt-2 pt-2 border-t" style={{ borderColor: '#bbf7d0' }}>
                <Icon name="Download" size={14} color="#16a34a" />
                <p className="text-xs" style={{ color: '#15803d' }}>Results CSV downloaded automatically</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 pb-6">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg border text-sm font-medium" style={{ borderColor: '#d1c4b0', color: '#374151', backgroundColor: '#ffffff' }}>
            {result ? 'Close' : 'Cancel'}
          </button>
          {result && (
            <button onClick={() => { setResult(null); if (fileRef?.current) fileRef.current.value = ''; }} className="flex-1 py-2.5 rounded-lg text-sm font-bold" style={{ backgroundColor: '#f59e0b', color: '#000000' }}>
              Upload Another
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Add Truck Modal ──────────────────────────────────────────────────────────
const statusOptions = [
  { value: 'Available', label: 'Available' },
  { value: 'Busy', label: 'Busy' },
];

const AddTruckModal = ({ onClose, onAdd }) => {
  const [form, setForm] = useState({
    vehicleNumber: '',
    capacityMT: '',
    currentLocation: '',
    ratePerTon: '',
    status: 'Available',
    verified: false,
  });
  const [error, setError] = useState('');

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (!form?.vehicleNumber?.trim()) { setError('Vehicle number is required.'); return; }
    if (!form?.capacityMT || isNaN(form?.capacityMT) || Number(form?.capacityMT) <= 0) { setError('Enter a valid capacity.'); return; }
    if (!form?.currentLocation?.trim()) { setError('Location is required.'); return; }
    if (!form?.ratePerTon || isNaN(form?.ratePerTon) || Number(form?.ratePerTon) <= 0) { setError('Enter a valid rate per ton.'); return; }
    setError('');
    onAdd({ ...form, capacityMT: Number(form?.capacityMT), ratePerTon: Number(form?.ratePerTon) });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
      <div className="rounded-2xl shadow-xl w-full max-w-md" style={{ backgroundColor: '#fdf6ec', border: '1px solid #e5d9cc' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: '#e5d9cc', backgroundColor: '#f8f1e9', borderRadius: '1rem 1rem 0 0' }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: '#fef3c7' }}>
              <Icon name="Truck" size={18} color="#d97706" />
            </div>
            <h2 className="text-base font-bold" style={{ color: '#1f2937' }}>Add New Truck</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100">
            <Icon name="X" size={18} color="#6b7280" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: '#374151' }}>Vehicle Number <span style={{ color: '#dc2626' }}>*</span></label>
            <input type="text" placeholder="e.g. MH12AB1234" value={form?.vehicleNumber} onChange={e => handleChange('vehicleNumber', e?.target?.value?.toUpperCase())} className="w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" style={{ borderColor: '#e5d9cc', backgroundColor: '#faf7f2', color: '#1f2937' }} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: '#374151' }}>Capacity (MT) <span style={{ color: '#dc2626' }}>*</span></label>
            <input type="number" placeholder="e.g. 25" min="1" max="35" value={form?.capacityMT} onChange={e => handleChange('capacityMT', e?.target?.value)} className="w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" style={{ borderColor: '#e5d9cc', backgroundColor: '#faf7f2', color: '#1f2937' }} />
            <p className="text-xs mt-1" style={{ color: '#9ca3af' }}>Average Truck Capacity 25T to 35T</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: '#374151' }}>Current Location (City, State) <span style={{ color: '#dc2626' }}>*</span></label>
            <input type="text" placeholder="e.g. Pune, Maharashtra" value={form?.currentLocation} onChange={e => handleChange('currentLocation', e?.target?.value)} className="w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" style={{ borderColor: '#e5d9cc', backgroundColor: '#faf7f2', color: '#1f2937' }} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: '#374151' }}>Rate per Ton (₹) <span style={{ color: '#dc2626' }}>*</span></label>
            <input type="number" placeholder="e.g. 1200" min="1" value={form?.ratePerTon} onChange={e => handleChange('ratePerTon', e?.target?.value)} className="w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" style={{ borderColor: '#e5d9cc', backgroundColor: '#faf7f2', color: '#1f2937' }} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: '#374151' }}>Status</label>
            <select value={form?.status} onChange={e => handleChange('status', e?.target?.value)} className="w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" style={{ borderColor: '#e5d9cc', backgroundColor: '#faf7f2', color: '#1f2937' }}>
              {statusOptions?.map(opt => (<option key={opt?.value} value={opt?.value}>{opt?.label}</option>))}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <input type="checkbox" id="verified-toggle" checked={form?.verified} onChange={e => handleChange('verified', e?.target?.checked)} className="w-4 h-4 rounded accent-amber-500 cursor-pointer" />
            <label htmlFor="verified-toggle" className="text-sm font-medium cursor-pointer" style={{ color: '#374151' }}>Mark as Verified</label>
          </div>
          {error && <p className="text-xs" style={{ color: '#dc2626' }}>{error}</p>}
        </div>
        <div className="flex gap-3 px-6 pb-6">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg border text-sm font-medium" style={{ borderColor: '#d1c4b0', color: '#374151' }}>Cancel</button>
          <button onClick={handleSubmit} className="flex-1 py-2.5 rounded-lg text-sm font-bold" style={{ backgroundColor: '#d97706', color: '#ffffff' }}>Add Truck</button>
        </div>
      </div>
    </div>
  );
};

// ─── Edit Truck Modal ─────────────────────────────────────────────────────────
const EditTruckModal = ({ truck, onClose, onSave }) => {
  const [form, setForm] = useState({
    vehicleNumber: truck?.vehicleNumber || '',
    capacityMT: truck?.capacityMT || '',
    currentLocation: truck?.currentLocation || '',
    ratePerTon: truck?.ratePerTon || (truck?.ratePerKg ? Math.round(truck?.ratePerKg * 1000) : ''),
    status: truck?.status || 'Available',
    verified: truck?.verified || false,
    offerId: truck?.offerId || truck?.offer_id || '',
  });
  const [error, setError] = useState('');
  const [offerOptions, setOfferOptions] = useState([]);
  const [loadingOffers, setLoadingOffers] = useState(false);

  // Fetch active/approved bookings with remaining tonnage calculation
  useEffect(() => {
    const fetchOffers = async () => {
      setLoadingOffers(true);
      try {
        // Fetch buyer_bookings with relevant statuses
        let query = supabase
          ?.from('buyer_bookings')
          ?.select('id, offer_id, buyer_email, quantity, grade, factory_name, status, booking_type')
          ?.in('status', ['approved', 'active', 'confirmed', 'pending_approval', 'do_generated'])          
          ?.not('offer_id', 'is', null)
          ?.neq('offer_id', '');

        // If truck has a linked buyer_email, filter to that buyer's bookings
        if (truck?.buyerEmail || truck?.buyer_email) {
          query = query?.eq('buyer_email', truck?.buyerEmail || truck?.buyer_email);
        }

        const { data: bookings, error: fetchErr } = await query
          ?.order('created_at', { ascending: false })
          ?.limit(100);

        if (fetchErr) {
          console.error('EditTruckModal fetch bookings error:', fetchErr);
          setLoadingOffers(false);
          return;
        }

        if (!bookings || bookings?.length === 0) {
          setOfferOptions([]);
          setLoadingOffers(false);
          return;
        }

        // Fetch already-assigned quantities per booking from order_truck_assignments
        const bookingIds = bookings?.map(b => b?.id);
        const { data: assignments } = await supabase
          ?.from('order_truck_assignments')
          ?.select('booking_id, assigned_quantity')
          ?.in('booking_id', bookingIds);

        // Build a map: booking_id → total assigned quantity
        const assignedMap = {};
        assignments?.forEach(a => {
          assignedMap[a?.booking_id] = (assignedMap?.[a?.booking_id] || 0) + Number(a?.assigned_quantity || 0);
        });

        // Deduplicate by offer_id, keeping the one with most remaining tonnage
        const offerMap = {};
        bookings?.forEach(b => {
          const offerId = b?.offer_id;
          if (!offerId) return;
          const booked = Number(b?.quantity || 0);
          const assigned = assignedMap?.[b?.id] || 0;
          const remaining = Math.max(0, booked - assigned);
          const emailPrefix = (b?.buyer_email || '')?.slice(0, 4)?.toUpperCase();

          if (!offerMap?.[offerId] || remaining > (offerMap?.[offerId]?.remaining || 0)) {
            offerMap[offerId] = {
              id: b?.id,
              offer_id: offerId,
              buyer_email: b?.buyer_email || '',
              buyer_email_prefix: emailPrefix,
              quantity: booked,
              assigned: assigned,
              remaining: remaining,
              grade: b?.grade || '',
              factory_name: b?.factory_name || '',
              status: b?.status || '',
              booking_type: b?.booking_type || '',
            };
          }
        });

        setOfferOptions(Object.values(offerMap));
      } catch (e) {
        console.error('EditTruckModal fetch offers error:', e);
      }
      setLoadingOffers(false);
    };
    fetchOffers();
  }, [truck?.id]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (!form?.vehicleNumber?.trim()) { setError('Vehicle number is required.'); return; }
    if (!form?.capacityMT || isNaN(form?.capacityMT) || Number(form?.capacityMT) <= 0) { setError('Enter a valid capacity.'); return; }
    if (!form?.currentLocation?.trim()) { setError('Location is required.'); return; }
    if (!form?.ratePerTon || isNaN(form?.ratePerTon) || Number(form?.ratePerTon) <= 0) { setError('Enter a valid rate per ton.'); return; }
    setError('');
    onSave(truck?.id, {
      ...form,
      capacityMT: Number(form?.capacityMT),
      ratePerTon: Number(form?.ratePerTon),
      offerId: form?.offerId || null,
    });
    onClose();
  };

  // Find selected offer details for contextual summary
  const selectedOffer = offerOptions?.find(o => o?.offer_id === form?.offerId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
      <div className="rounded-2xl shadow-xl w-full max-w-md overflow-y-auto" style={{ backgroundColor: '#fdf6ec', border: '1px solid #e5d9cc', maxHeight: '90vh' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: '#e5d9cc', backgroundColor: '#f8f1e9', borderRadius: '1rem 1rem 0 0' }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: '#fef3c7' }}>
              <Icon name="Pencil" size={18} color="#d97706" />
            </div>
            <h2 className="text-base font-bold" style={{ color: '#1f2937' }}>Edit Truck</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100">
            <Icon name="X" size={18} color="#6b7280" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: '#374151' }}>Vehicle Number <span style={{ color: '#dc2626' }}>*</span></label>
            <input type="text" placeholder="e.g. MH12AB1234" value={form?.vehicleNumber} onChange={e => handleChange('vehicleNumber', e?.target?.value?.toUpperCase())} className="w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" style={{ borderColor: '#e5d9cc', backgroundColor: '#faf7f2', color: '#1f2937' }} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: '#374151' }}>Capacity (MT) <span style={{ color: '#dc2626' }}>*</span></label>
            <input type="number" placeholder="e.g. 25" min="1" max="35" value={form?.capacityMT} onChange={e => handleChange('capacityMT', e?.target?.value)} className="w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" style={{ borderColor: '#e5d9cc', backgroundColor: '#faf7f2', color: '#1f2937' }} />
            <p className="text-xs mt-1" style={{ color: '#9ca3af' }}>Average Truck Capacity 25T to 35T</p>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: '#374151' }}>Current Location (City, State) <span style={{ color: '#dc2626' }}>*</span></label>
            <input type="text" placeholder="e.g. Pune, Maharashtra" value={form?.currentLocation} onChange={e => handleChange('currentLocation', e?.target?.value)} className="w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" style={{ borderColor: '#e5d9cc', backgroundColor: '#faf7f2', color: '#1f2937' }} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: '#374151' }}>Rate per Ton (₹) <span style={{ color: '#dc2626' }}>*</span></label>
            <input type="number" placeholder="e.g. 1200" min="1" value={form?.ratePerTon} onChange={e => handleChange('ratePerTon', e?.target?.value)} className="w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" style={{ borderColor: '#e5d9cc', backgroundColor: '#faf7f2', color: '#1f2937' }} />
          </div>

          {/* Offer ID Dropdown */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: '#374151' }}>
              Offer ID
              {loadingOffers && (
                <span className="ml-2 inline-flex items-center gap-1 text-xs font-normal" style={{ color: '#9ca3af' }}>
                  <span className="w-3 h-3 border-2 border-amber-400 border-t-transparent rounded-full animate-spin inline-block" />
                  Loading…
                </span>
              )}
            </label>
            <select
              value={form?.offerId}
              onChange={e => handleChange('offerId', e?.target?.value)}
              className="w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              style={{ borderColor: '#e5d9cc', backgroundColor: '#faf7f2', color: form?.offerId ? '#1f2937' : '#9ca3af' }}
              disabled={loadingOffers}
            >
              <option value="">— No Offer ID assigned —</option>
              {offerOptions?.map(o => (
                <option key={o?.id} value={o?.offer_id}>
                  {o?.offer_id} - {o?.buyer_email_prefix} - {o?.remaining}T
                </option>
              ))}
              {!loadingOffers && offerOptions?.length === 0 && (
                <option disabled value="">No approved/active bookings found</option>
              )}
            </select>

            {/* Contextual summary for selected offer */}
            {selectedOffer && (
              <div className="mt-1.5 px-3 py-2.5 rounded-lg text-xs space-y-1" style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
                <p style={{ color: '#78350f' }}>
                  <span className="font-semibold" style={{ color: '#92400e' }}>Buyer: </span>
                  {selectedOffer?.buyer_email}
                </p>
                <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                  <span style={{ color: '#78350f' }}>
                    <span className="font-semibold" style={{ color: '#92400e' }}>Booked: </span>
                    {selectedOffer?.quantity} MT
                  </span>
                  <span style={{ color: '#78350f' }}>
                    <span className="font-semibold" style={{ color: '#92400e' }}>Assigned: </span>
                    {selectedOffer?.assigned} MT
                  </span>
                  <span style={{ color: selectedOffer?.remaining > 0 ? '#166534' : '#dc2626', fontWeight: 600 }}>
                    <span className="font-semibold">Remaining: </span>
                    {selectedOffer?.remaining} MT
                  </span>
                </div>
                <p style={{ color: '#78350f' }}>
                  <span className="font-semibold" style={{ color: '#92400e' }}>Factory: </span>
                  {selectedOffer?.factory_name} · {selectedOffer?.grade} ·{' '}
                  <span className="capitalize">{selectedOffer?.status}</span>
                </p>
              </div>
            )}
            <p className="text-xs mt-1" style={{ color: '#9ca3af' }}>
              Shows approved/active bookings. Format: Offer ID - Buyer Prefix - Remaining Tonnage. Rate per Ton above applies to this vehicle for the selected Offer ID.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: '#374151' }}>Status</label>
            <select value={form?.status} onChange={e => handleChange('status', e?.target?.value)} className="w-full rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" style={{ borderColor: '#e5d9cc', backgroundColor: '#faf7f2', color: '#1f2937' }}>
              {statusOptions?.map(opt => (<option key={opt?.value} value={opt?.value}>{opt?.label}</option>))}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <input type="checkbox" id="edit-verified-toggle" checked={form?.verified} onChange={e => handleChange('verified', e?.target?.checked)} className="w-4 h-4 rounded accent-amber-500 cursor-pointer" />
            <label htmlFor="edit-verified-toggle" className="text-sm font-medium cursor-pointer" style={{ color: '#374151' }}>Mark as Verified</label>
          </div>
          {error && <p className="text-xs" style={{ color: '#dc2626' }}>{error}</p>}
        </div>
        <div className="flex gap-3 px-6 pb-6">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg border text-sm font-medium" style={{ borderColor: '#d1c4b0', color: '#374151' }}>Cancel</button>
          <button onClick={handleSubmit} className="flex-1 py-2.5 rounded-lg text-sm font-bold" style={{ backgroundColor: '#d97706', color: '#ffffff' }}>Save Changes</button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const VehicleManagementTab = () => {
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [editTruck, setEditTruck] = useState(null);
  const [removeConfirm, setRemoveConfirm] = useState({ show: false, truckId: null });
  const [search, setSearch] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '' });
  const [selectedTruck, setSelectedTruck] = useState(null);

  useEffect(() => {
    const unsub = subscribeTrucks((updated) => {
      setTrucks(updated);
      setLoading(false);
    });
    return unsub;
  }, []);

  const showNotification = (message) => {
    setNotification({ show: true, message });
    setTimeout(() => setNotification({ show: false, message: '' }), 4000);
  };

  const handleAdd = async (truckData) => {
    await addTruck(truckData);
    showNotification('Truck added successfully.');
  };

  const handleEdit = async (truckId, updates) => {
    await updateTruck(truckId, updates);
    showNotification('Truck updated successfully.');
  };

  const handleStatusToggle = async (truck) => {
    const newStatus = truck?.status === 'Available' ? 'Busy' : 'Available';
    await updateTruck(truck?.id, { status: newStatus });
  };

  const handleRemove = (truckId) => {
    setRemoveConfirm({ show: true, truckId });
  };

  const confirmRemove = async () => {
    await removeTruck(removeConfirm?.truckId);
    showNotification('Truck removed successfully.');
    setRemoveConfirm({ show: false, truckId: null });
  };

  // ── CSV Upload handler (async — writes to Supabase) ──────────────────────
  const handleCSVUpload = async (rows) => {
    let updated = 0;
    let skipped = 0;
    let invalid = 0;
    const resultRows = [];

    for (const row of rows) {
      const remark = (row?.Remark || '')?.trim()?.toLowerCase();
      const vehicleNumber = (row?.VehicleNumber || '')?.trim()?.toUpperCase();
      const capacityMT = parseFloat(row?.Capacity_MT);
      const ratePerTon = parseFloat(row?.Rate_per_Ton);

      if (!vehicleNumber) {
        invalid++;
        resultRows?.push({ status: 'Error', message: 'Missing required field: VehicleNumber' });
        continue;
      }

      if (!remark?.includes('update')) {
        skipped++;
        resultRows?.push({ status: 'Skipped', message: 'No changes — Remark is blank or not "update"' });
        continue;
      }

      const csvStatus = (row?.Status || '')?.trim()?.toLowerCase() === 'busy' ? 'Busy' : 'Available';
      const verifiedRaw = (row?.Verified || '')?.trim()?.toLowerCase();
      const verified = verifiedRaw === 'yes' || verifiedRaw === 'true' || verifiedRaw === '1';
      const currentLocation = (row?.CurrentLocation || '')?.trim();

      const result = await upsertTruckByVehicleNumber({
        vehicleNumber,
        capacityMT: !isNaN(capacityMT) && capacityMT > 0 ? capacityMT : 20,
        currentLocation,
        ratePerTon: !isNaN(ratePerTon) && ratePerTon > 0 ? ratePerTon : 1200,
        status: csvStatus,
        verified,
      });
      if (result === null) {
        console.error('Truck upsert failed for vehicle:', vehicleNumber);
        invalid++;
        resultRows?.push({ status: 'Error', message: 'Database write failed — check console for details' });
      } else {
        updated++;
        resultRows?.push({ status: 'Updated', message: 'Saved to database successfully' });
      }
    }

    showNotification(`${updated} rows updated successfully, ${skipped} rows skipped.`);
    return { updated, skipped, invalid, resultRows };
  };

  const filtered = trucks?.filter(t =>
    !search ||
    t?.vehicleNumber?.toLowerCase()?.includes(search?.toLowerCase()) ||
    t?.currentLocation?.toLowerCase()?.includes(search?.toLowerCase())
  );

  return (
    <div>
      {/* CSV Upload Modal */}
      {showCSVModal && (
        <VehicleCSVUploadModal
          onClose={() => setShowCSVModal(false)}
          onUpload={handleCSVUpload}
        />
      )}

      {/* Edit Truck Modal */}
      {editTruck && (
        <EditTruckModal
          truck={editTruck}
          onClose={() => setEditTruck(null)}
          onSave={handleEdit}
        />
      )}

      {/* Remove Confirm Modal */}
      {removeConfirm?.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-sm w-full mx-4 shadow-xl">
            <div className="flex items-start gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Icon name="AlertTriangle" size={20} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-bold mb-1" style={{ color: '#1f2937' }}>Remove Truck</h3>
                <p className="text-sm" style={{ color: '#4b5563' }}>Are you sure you want to remove this truck? This cannot be undone.</p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setRemoveConfirm({ show: false, truckId: null })} className="px-4 py-2 text-sm font-semibold rounded-lg border-2 border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors">Cancel</button>
              <button onClick={confirmRemove} className="px-4 py-2 text-sm font-bold rounded-lg bg-red-600 text-white border-2 border-red-700 hover:bg-red-700 transition-colors">Remove</button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      {notification?.show && (
        <div className="fixed top-4 right-4 z-50 max-w-sm">
          <div className="rounded-lg p-4 border shadow-lg flex items-center gap-3 bg-green-50 border-green-200">
            <Icon name="CheckCircle" size={18} className="text-green-600" />
            <p className="text-sm font-medium text-green-900">{notification?.message}</p>
            <button onClick={() => setNotification({ show: false, message: '' })} className="ml-auto text-gray-400 hover:text-gray-600">
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Top bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="text-lg font-bold" style={{ color: '#1f2937' }}>Vehicle Management</h2>
          <p className="text-sm" style={{ color: '#6b7280' }}>Manage platform trucks — add, update status, and verify vehicles</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Download Sample CSV */}
          <button
            onClick={downloadSampleCSV}
            className="flex items-center gap-1.5 text-sm font-semibold underline"
            style={{ color: '#d97706' }}
          >
            <Icon name="Download" size={15} />
            Download Sample CSV
          </button>

          {/* Download Current Data CSV */}
          <button
            onClick={() => downloadCurrentDataCSV(trucks)}
            className="flex items-center gap-1.5 text-sm font-semibold underline"
            style={{ color: '#16a34a' }}
          >
            <Icon name="FileDown" size={15} />
            Download Current Data CSV
          </button>

          {/* Bulk Upload CSV */}
          <button
            onClick={() => setShowCSVModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold shadow-sm border-2 transition-colors hover:bg-amber-500"
            style={{ backgroundColor: '#f59e0b', color: '#000000', borderColor: '#d97706' }}
          >
            <Icon name="Upload" size={16} />
            Bulk Upload CSV
          </button>

          {/* Add New Truck */}
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold shadow-sm border-2 transition-colors hover:bg-gray-100"
            style={{ backgroundColor: '#ffffff', color: '#374151', borderColor: '#d1d5db' }}
          >
            <Icon name="Plus" size={16} />
            Add New Truck
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Icon name="Search" size={16} color="#9ca3af" className="absolute left-3 top-1/2 -translate-y-1/2" />
        <input type="text" placeholder="Search by vehicle number or location..." value={search} onChange={e => setSearch(e?.target?.value)} className="w-full pl-9 pr-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" style={{ borderColor: '#e5d9cc', backgroundColor: '#ffffff', color: '#1f2937' }} />
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-10">
          <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mr-3"></div>
          <p className="text-sm" style={{ color: '#6b7280' }}>Loading trucks…</p>
        </div>
      )}

      {!loading && (
        <>
          {/* Stats row */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="rounded-xl p-4 text-center" style={{ backgroundColor: '#ffffff', border: '1px solid #e5d9cc' }}>
              <p className="text-2xl font-bold" style={{ color: '#1f2937' }}>{trucks?.length}</p>
              <p className="text-xs" style={{ color: '#6b7280' }}>Total Trucks</p>
            </div>
            <div className="rounded-xl p-4 text-center" style={{ backgroundColor: '#d1fae5', border: '1px solid #a7f3d0' }}>
              <p className="text-2xl font-bold" style={{ color: '#065f46' }}>{trucks?.filter(t => t?.status === 'Available')?.length}</p>
              <p className="text-xs" style={{ color: '#065f46' }}>Available</p>
            </div>
            <div className="rounded-xl p-4 text-center" style={{ backgroundColor: '#fef3c7', border: '1px solid #fde68a' }}>
              <p className="text-2xl font-bold" style={{ color: '#92400e' }}>{trucks?.filter(t => t?.status === 'Busy')?.length}</p>
              <p className="text-xs" style={{ color: '#92400e' }}>Busy</p>
            </div>
          </div>

          {/* Table — desktop */}
          <div className="hidden sm:block rounded-xl border overflow-hidden" style={{ borderColor: '#e5d9cc' }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ backgroundColor: '#f8f1e9' }}>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: '#374151' }}>Vehicle Number</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: '#374151' }}>Capacity (MT)</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: '#374151' }}>Location</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: '#374151' }}>Status</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: '#374151' }}>Verified</th>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: '#374151' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered?.map((truck, idx) => (
                  <tr key={truck?.id} style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#faf7f2', borderTop: '1px solid #f0e8de' }}>
                    <td className="px-4 py-3 font-bold tracking-wide" style={{ color: '#1f2937' }}>
                      <button
                        onClick={() => setSelectedTruck(truck)}
                        className="font-bold tracking-wide hover:underline text-left"
                        style={{ color: '#1e40af' }}
                      >
                        {truck?.vehicleNumber}
                      </button>
                    </td>
                    <td className="px-4 py-3 font-semibold" style={{ color: '#1f2937' }}>{truck?.capacityMT} MT</td>
                    <td className="px-4 py-3" style={{ color: '#4b5563' }}>{truck?.currentLocation}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: truck?.status === 'Available' ? '#d1fae5' : '#fef3c7', color: truck?.status === 'Available' ? '#065f46' : '#92400e' }}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: truck?.status === 'Available' ? '#10b981' : '#f59e0b' }} />
                        {truck?.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {truck?.verified ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: '#059669' }}>
                          <Icon name="CheckCircle" size={14} color="#059669" /> Yes
                        </span>
                      ) : (
                        <span className="text-xs" style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleStatusToggle(truck)} className="text-xs px-3 py-1.5 rounded-lg font-medium border transition-colors hover:opacity-80" style={{ borderColor: truck?.status === 'Available' ? '#fde68a' : '#a7f3d0', color: truck?.status === 'Available' ? '#92400e' : '#065f46', backgroundColor: truck?.status === 'Available' ? '#fffbeb' : '#ecfdf5' }}>
                          {truck?.status === 'Available' ? 'Mark Busy' : 'Mark Available'}
                        </button>
                        <button onClick={() => setEditTruck(truck)} className="text-xs px-3 py-1.5 rounded-lg font-medium border transition-colors hover:opacity-80" style={{ borderColor: '#d1d5db', color: '#374151', backgroundColor: '#ffffff' }}>
                          Edit
                        </button>
                        <button onClick={() => handleRemove(truck?.id)} className="text-xs px-3 py-1.5 rounded-lg font-medium border transition-colors hover:opacity-80" style={{ borderColor: '#fca5a5', color: '#dc2626', backgroundColor: '#fef2f2' }}>
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered?.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-10" style={{ color: '#9ca3af' }}>No trucks found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Cards — mobile */}
          <div className="sm:hidden space-y-3">
            {filtered?.map(truck => (
              <div key={truck?.id} className="rounded-xl border p-4" style={{ backgroundColor: '#ffffff', borderColor: '#e5d9cc' }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold tracking-wide" style={{ color: '#1f2937' }}>{truck?.vehicleNumber}</span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: truck?.status === 'Available' ? '#d1fae5' : '#fef3c7', color: truck?.status === 'Available' ? '#065f46' : '#92400e' }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: truck?.status === 'Available' ? '#10b981' : '#f59e0b' }} />
                    {truck?.status}
                  </span>
                </div>
                <div className="text-sm space-y-1 mb-3">
                  <div className="flex justify-between">
                    <span style={{ color: '#6b7280' }}>Capacity</span>
                    <span className="font-semibold" style={{ color: '#1f2937' }}>{truck?.capacityMT} MT</span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: '#6b7280' }}>Location</span>
                    <span className="font-semibold text-right" style={{ color: '#1f2937', maxWidth: '60%' }}>{truck?.currentLocation}</span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: '#6b7280' }}>Verified</span>
                    <span className="font-semibold" style={{ color: truck?.verified ? '#059669' : '#9ca3af' }}>{truck?.verified ? '✓ Yes' : '—'}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleStatusToggle(truck)} className="flex-1 text-xs py-2 rounded-lg font-medium border" style={{ borderColor: truck?.status === 'Available' ? '#fde68a' : '#a7f3d0', color: truck?.status === 'Available' ? '#92400e' : '#065f46', backgroundColor: truck?.status === 'Available' ? '#fffbeb' : '#ecfdf5' }}>
                    {truck?.status === 'Available' ? 'Mark Busy' : 'Mark Available'}
                  </button>
                  <button onClick={() => setEditTruck(truck)} className="flex-1 text-xs py-2 rounded-lg font-medium border" style={{ borderColor: '#d1d5db', color: '#374151', backgroundColor: '#ffffff' }}>
                    Edit
                  </button>
                  <button onClick={() => handleRemove(truck?.id)} className="flex-1 text-xs py-2 rounded-lg font-medium border" style={{ borderColor: '#fca5a5', color: '#dc2626', backgroundColor: '#fef2f2' }}>
                    Remove
                  </button>
                </div>
              </div>
            ))}
            {filtered?.length === 0 && (
              <div className="text-center py-10" style={{ color: '#9ca3af' }}>No trucks found.</div>
            )}
          </div>
        </>
      )}

      {showAddModal && (
        <AddTruckModal onClose={() => setShowAddModal(false)} onAdd={handleAdd} />
      )}

      {selectedTruck && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between" style={{ borderColor: '#e5e7eb' }}>
              <h3 className="text-xl font-bold" style={{ color: '#1f2937' }}>
                Truck Details
              </h3>
              <button onClick={() => setSelectedTruck(null)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Vehicle Info */}
              <div>
                <h4 className="text-sm font-semibold mb-3" style={{ color: '#6b7280' }}>VEHICLE INFORMATION</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Vehicle Number</div>
                    <div className="font-bold text-lg" style={{ color: '#1f2937' }}>{selectedTruck?.vehicleNumber}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Capacity</div>
                    <div className="font-semibold">{selectedTruck?.capacityMT} MT</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Rate</div>
                    <div className="font-semibold">₹{selectedTruck?.ratePerTon}/T</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Status</div>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      selectedTruck?.status === 'Available' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {selectedTruck?.status}
                    </span>
                  </div>
                  <div className="col-span-2">
                    <div className="text-xs text-gray-500 mb-1">Current Location</div>
                    <div className="font-semibold">{selectedTruck?.currentLocation}</div>
                  </div>
                </div>
              </div>

              {/* Transport Banner */}
              {selectedTruck?.transporter_name && (
                <div className="p-4 rounded-xl" style={{ backgroundColor: '#fef3c7', border: '1px solid #fde68a' }}>
                  <div className="text-xs font-semibold mb-1" style={{ color: '#92400e' }}>TRANSPORT BANNER</div>
                  <div className="text-lg font-bold" style={{ color: '#78350f' }}>🚛 {selectedTruck?.transporter_name}</div>
                  <div className="text-xs text-gray-600 mt-1">Painted on truck body</div>
                </div>
              )}

              {/* Current Agent */}
              {selectedTruck?.agent_name && (
                <div>
                  <h4 className="text-sm font-semibold mb-3" style={{ color: '#6b7280' }}>CURRENT AGENT AFFILIATION</h4>
                  <div className="p-4 rounded-xl border" style={{ borderColor: '#e5e7eb', backgroundColor: '#f9fafb' }}>
                    <div className="font-bold text-base mb-1">{selectedTruck?.agent_name}</div>
                    {selectedTruck?.agent_mobile && (
                      <div className="text-sm text-gray-600">📱 {selectedTruck?.agent_mobile}</div>
                    )}
                    <div className="text-xs text-gray-500 mt-2">Agent can change weekly based on who brings the booking</div>
                  </div>
                </div>
              )}

              {/* Driver Info */}
              {(selectedTruck?.driver_name || selectedTruck?.driver_mobile) && (
                <div>
                  <h4 className="text-sm font-semibold mb-3" style={{ color: '#6b7280' }}>DRIVER INFORMATION</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedTruck?.driver_name && (
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Driver Name</div>
                        <div className="font-semibold">{selectedTruck?.driver_name}</div>
                      </div>
                    )}
                    {selectedTruck?.driver_mobile && (
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Mobile</div>
                        <div className="font-semibold">📱 {selectedTruck?.driver_mobile}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Verification Status */}
              <div>
                <h4 className="text-sm font-semibold mb-3" style={{ color: '#6b7280' }}>VERIFICATION</h4>
                <div className="flex items-center gap-2">
                  {selectedTruck?.verified ? (
                    <>
                      <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-green-700 font-semibold">Verified</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <span className="text-yellow-700 font-semibold">Pending Verification</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleManagementTab;
